<script setup lang="ts">
import { nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WizardSubmitStep from '@/modules/document/document-master/WizardSubmitStep.vue';
import { useSubmitRunner } from '@/modules/document/document-master/composables/use-submit-runner.ts';
import {
  COUNTERPARTY_TASK_STEP,
  useCounterpartyExecutionTasks,
} from '@/modules/counterparty/counterparty-master/composables/use-counterparty-execution-tasks.ts';
import { useWizard, useWizardOptionalStep, useWizardStep } from '@/providers/wizard.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { DirectoryNameDuplicateError } from '@/modules/counterparty/counterparty-master/utils/find-directory-name-duplicate.util.ts';

const EXECUTION_SUCCESS_DELAY_MS = 1200;

const { t } = useI18n();
const { next, goTo, steps, setShowForceClose } = useWizard();
const { isLoading } = useWizardStep();

useWizardOptionalStep();
const tasks = useCounterpartyExecutionTasks();

let navigatedBack = false;
let successTimeout: ReturnType<typeof setTimeout> | undefined;

onUnmounted(() => {
  if (successTimeout !== undefined) {
    clearTimeout(successTimeout);
  }
});

function goToFailedStep(taskCode: string) {
  const stepCode = COUNTERPARTY_TASK_STEP[taskCode];

  if (!stepCode) {
    return false;
  }

  const index = steps.value.findIndex((step) => step.code === stepCode);

  if (index < 0) {
    return false;
  }

  goTo(index);
  return true;
}

const { items } = useSubmitRunner(tasks, {
  isLoading,
  onForceClose: () => {
    if (!navigatedBack) {
      setShowForceClose(true);
    }
  },
  onSuccess: async () => {
    await nextTick();
    successTimeout = setTimeout(() => next(), EXECUTION_SUCCESS_DELAY_MS);
  },
  onError: (task, error) => {
    if (error instanceof DirectoryNameDuplicateError) {
      notification.error(`counterparty.notification.error.${error.field}.duplicate`);
    } else {
      const step = typeof task.title === 'string' ? t(task.title) : t(task.title.key, task.title.params);
      notification.error({ key: 'counterparty.notification.error.addAtStep', params: { step } }, error);
    }

    navigatedBack = goToFailedStep(task.code);
  },
});
</script>

<template>
  <WizardSubmitStep :items="items" />
</template>
